import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { WINDOW_PRESETS, type InputEvent as RemoteInputEvent, type WindowSize } from '@br/protocol';
import {
  Badge,
  Button,
  ConfirmDialog,
  CopyButton,
  Input,
  Modal,
  Select,
  cx,
} from '../components/ui';
import { BrowserDialogModal } from './BrowserDialogModal';
import { useToast } from '../components/toast';
import {
  MODE_LABEL,
  RUNTIME_LABEL,
  RUNTIME_TONE,
  STATUS_LABEL,
  STATUS_TONE,
  errorText,
} from '../lib/labels';
import { AddressBar } from './AddressBar';
import { FileChooserModal } from './FileChooserModal';
import { ViewerOverlay } from './Overlay';
import { Presence } from './Presence';
import { ScreenCanvas } from './ScreenCanvas';
import { TabStrip } from './TabStrip';
import { RequestError, type ViewerEvent, type ViewerRequest } from './socket';
import { useViewer } from './useViewer';

export interface ViewerProps {
  profileId: string;
  variant: 'operator' | 'customer';
  initialTabId?: string;
  /** Associates an operator view with one support workflow; required for customer views. */
  supportSessionId?: string;
  isAdmin?: boolean;
  /** Shown until the snapshot arrives */
  title?: string;
  /** Left-most header button */
  exitLabel: string;
  onExit: () => void;
  /** Customer only: extra header content (e.g. the participant label) */
  headerNote?: ReactNode;
}

function presetLabel(label: string): string {
  return label
    .replace('Desktop', '데스크톱')
    .replace('Narrow', '좁은 화면')
    .replace('Mobile-ish', '모바일형');
}

const CONTROL_REQUEST_TTL_MS = 30_000;

/** Shared viewer for operators and customers; the variant only changes what is allowed/visible. */
export function Viewer({
  profileId,
  variant,
  initialTabId,
  supportSessionId,
  isAdmin,
  title,
  exitLabel,
  onExit,
  headerNote,
}: ViewerProps) {
  const toast = useToast();
  const wasController = useRef(false);
  const [done, setDone] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [newTabOpen, setNewTabOpen] = useState(false);
  const [newTabUrl, setNewTabUrl] = useState('');
  const [confirmDone, setConfirmDone] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [clipboardText, setClipboardText] = useState('');
  const [clipboardMode, setClipboardMode] = useState<'copy' | 'paste' | null>(null);

  const onEvent = useCallback(
    (e: ViewerEvent) => {
      switch (e.type) {
        case 'notice':
          toast.push({ message: e.message, level: e.level === 'warn' ? 'warning' : e.level });
          return;
        case 'control.changed':
          if (e.isMe && !wasController.current) toast.success('제어권을 받았습니다');
          else if (!e.isMe && wasController.current)
            toast.info(
              e.reason === 'taken'
                ? '관리자가 제어권을 가져갔습니다'
                : '제어권이 다른 참여자에게 넘어갔습니다',
            );
          wasController.current = e.isMe;
          if (!e.isMe) setRequesting(false);
          return;
        case 'control.denied':
          setRequesting(false);
          toast.warning('제어 요청이 거부되었습니다');
          return;
        case 'navigation_blocked':
          toast.warning('허용되지 않은 주소로의 이동이 차단되었습니다');
          return;
        case 'kicked':
          return;
        case 'joined':
          setRequesting(false);
          return;
      }
    },
    [toast],
  );

  const { socket, frames, state } = useViewer(profileId, {
    tabId: initialTabId,
    supportSessionId,
    onEvent,
  });
  const snap = state.snapshot;
  const me = state.participantId;
  const isController = Boolean(me) && snap?.controllerId === me;
  // Control is reserved for a disconnected holder; a request then takes it immediately.
  const holderAway = Boolean(snap?.controller) && !snap?.controller?.connected;
  const joined = state.connection === 'joined';
  const runtimeOpen = snap?.runtime === 'open';
  const nodeOnline = snap?.nodeOnline ?? false;
  const tabId = state.tabId;
  const stream = tabId ? state.streams[tabId] : undefined;
  const inputEnabled =
    joined && isController && runtimeOpen && nodeOnline && Boolean(tabId) && !done;
  const dimmed = !runtimeOpen || !nodeOnline || (stream !== undefined && stream.state !== 'active');
  const currentTab = snap?.tabs.find((t) => t.tabId === tabId);
  const can = state.can;
  const activeDialog = snap?.dialogs?.find((d) => d.tabId === tabId) ?? snap?.dialogs?.[0];

  const copyRemote = () => {
    if (!inputEnabled || !tabId) return;
    const selected = socket.request<{ text: string }>({ t: 'clipboard.read', tabId });
    // Construct the clipboard write during the user gesture (also works in Safari).
    const write =
      typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write
        ? navigator.clipboard.write([
            new ClipboardItem({
              'text/plain': selected.then(({ text }) => new Blob([text], { type: 'text/plain' })),
            }),
          ])
        : Promise.reject(new Error('clipboard unavailable'));
    void write
      .then(() => toast.success('선택한 내용을 복사했습니다'))
      .catch(() => {
        void selected
          .then(({ text }) => {
            setClipboardText(text);
            setClipboardMode('copy');
          })
          .catch((err: unknown) => toast.error(err instanceof Error ? err.message : '복사 실패'));
      });
  };

  useEffect(() => {
    wasController.current = isController;
  }, [isController]);

  useEffect(() => {
    if (variant !== 'customer') return;
    const status = snap?.supportSession?.status;
    if (status === 'customer_done' || status === 'operator_working' || status === 'completed') {
      setDone(true);
    }
  }, [snap?.supportSession?.status, variant]);

  // Incoming control requests expire server-side after 30 s; mirror that locally.
  useEffect(() => {
    if (!state.request) return;
    const t = setTimeout(() => socket.clearRequest(), CONTROL_REQUEST_TTL_MS);
    return () => clearTimeout(t);
  }, [state.request, socket]);

  const run = useCallback(
    async (req: ViewerRequest, key?: string): Promise<boolean> => {
      if (key) setBusy(key);
      try {
        await socket.request(req);
        return true;
      } catch (err) {
        toast.error(
          err instanceof RequestError ? errorText(err.code, err.message) : errorText('internal'),
        );
        return false;
      } finally {
        if (key) setBusy(null);
      }
    },
    [socket, toast],
  );

  const onInput = useCallback((e: RemoteInputEvent) => socket.sendInput(e), [socket]);

  const selectTab = (id: string) =>
    socket.selectTab(id).catch((err: unknown) => {
      toast.error(
        err instanceof RequestError ? errorText(err.code, err.message) : errorText('internal'),
      );
    });

  const requestControl = async () => {
    setRequesting(true);
    const ok = await run({ t: 'control.request' });
    if (!ok) setRequesting(false);
  };

  const headerBadges = snap && (
    <>
      {snap.supportSession && (
        <Badge tone={STATUS_TONE[snap.supportSession.status]}>
          {STATUS_LABEL[snap.supportSession.status]}
        </Badge>
      )}
      <Badge tone={RUNTIME_TONE[snap.runtime]}>브라우저 {RUNTIME_LABEL[snap.runtime]}</Badge>
      {!snap.nodeOnline && <Badge tone="danger">노드 오프라인</Badge>}
      {state.connection !== 'joined' && (
        <Badge tone="warning">
          연결 {state.connection === 'reconnecting' ? '재시도 중' : '중'}
        </Badge>
      )}
    </>
  );

  return (
    <div className="flex h-full flex-col bg-canvas">
      <header className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-line bg-surface px-3 py-2">
        <Button size="sm" variant="ghost" onClick={onExit}>
          ← {exitLabel}
        </Button>
        <div className="flex min-w-0 items-center gap-2">
          <h1 className="truncate text-sm font-semibold text-ink">
            {snap?.name ?? title ?? '브라우저 프로필'}
          </h1>
          {headerBadges}
        </div>
        {headerNote && <span className="text-xs text-ink-muted">{headerNote}</span>}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Presence
            participants={snap?.participants ?? []}
            controllerId={snap?.controllerId}
            controller={snap?.controller}
            meId={me}
          />
          {joined && !done && (
            <div className="flex items-center gap-1">
              {isController ? (
                <Button
                  size="sm"
                  onClick={() => run({ t: 'control.release' }, 'release')}
                  loading={busy === 'release'}
                >
                  제어 해제
                </Button>
              ) : (
                <Button size="sm" variant="primary" onClick={requestControl} disabled={requesting}>
                  {requesting ? '요청 중…' : holderAway ? '제어 가져오기' : '제어 요청'}
                </Button>
              )}
              {can.forceControl && isAdmin && !isController && (
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => run({ t: 'control.take' }, 'take')}
                  loading={busy === 'take'}
                >
                  강제 제어
                </Button>
              )}
            </div>
          )}
          {variant === 'operator' && joined && snap && (
            <div className="flex items-center gap-1">
              {can.resize && (
                <Select
                  aria-label="창 크기"
                  className="h-8 w-auto py-0 text-xs"
                  disabled={!isController || !runtimeOpen}
                  value={`${snap.size.width}x${snap.size.height}`}
                  onChange={(e) => {
                    const [w, h] = e.target.value.split('x').map(Number);
                    if (w && h)
                      void run(
                        { t: 'window.resize', size: { width: w, height: h } as WindowSize },
                        'resize',
                      );
                  }}
                >
                  {!WINDOW_PRESETS.some(
                    (p) => p.width === snap.size.width && p.height === snap.size.height,
                  ) && (
                    <option value={`${snap.size.width}x${snap.size.height}`}>
                      사용자 지정 {snap.size.width}×{snap.size.height}
                    </option>
                  )}
                  {WINDOW_PRESETS.map((p) => (
                    <option key={p.label} value={`${p.width}x${p.height}`}>
                      {presetLabel(p.label)}
                    </option>
                  ))}
                </Select>
              )}
              {snap.availableModes.length > 1 && (
                <Select
                  aria-label="전송 모드"
                  className="h-8 w-auto py-0 text-xs"
                  value={state.mode}
                  onChange={() => {}}
                >
                  {snap.availableModes.map((m) => (
                    <option key={m} value={m}>
                      {MODE_LABEL[m]}
                    </option>
                  ))}
                </Select>
              )}
              {can.openBrowser &&
                (runtimeOpen || snap.runtime === 'launching' ? (
                  <Button
                    size="sm"
                    onClick={() => run({ t: 'browser.close' }, 'close')}
                    loading={busy === 'close'}
                    disabled={!nodeOnline}
                  >
                    브라우저 닫기
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => run({ t: 'browser.open' }, 'open')}
                    loading={busy === 'open'}
                    disabled={!nodeOnline}
                  >
                    브라우저 열기
                  </Button>
                ))}
            </div>
          )}
          {variant === 'customer' && joined && !done && (
            <Button size="sm" variant="primary" onClick={() => setConfirmDone(true)}>
              완료
            </Button>
          )}
        </div>
      </header>

      {state.request && isController && (
        <div className="flex items-center gap-3 border-b border-amber-200 bg-warning-soft px-3 py-2 text-sm text-amber-900">
          <span className="font-medium">
            {state.request.name}
            {state.request.kind === 'customer' ? ' (고객)' : ' (운영자)'}
          </span>
          님이 제어권을 요청했습니다.
          <div className="ml-auto flex gap-1">
            <Button
              size="sm"
              variant="primary"
              onClick={async () => {
                const pid = state.request!.participantId;
                if (await run({ t: 'control.grant', participantId: pid }, 'grant'))
                  socket.clearRequest();
              }}
              loading={busy === 'grant'}
            >
              승인
            </Button>
            <Button
              size="sm"
              onClick={async () => {
                const pid = state.request!.participantId;
                if (await run({ t: 'control.deny', participantId: pid }, 'deny'))
                  socket.clearRequest();
              }}
              loading={busy === 'deny'}
            >
              거부
            </Button>
          </div>
        </div>
      )}

      <TabStrip
        tabs={snap?.tabs ?? []}
        activeTabId={snap?.activeTabId}
        viewingTabId={tabId}
        canClose={can.closeTab && isController && runtimeOpen && !done}
        canCreate={can.createTab && isController && runtimeOpen}
        onSelect={(id) => void selectTab(id)}
        onClose={(id) => void run({ t: 'tab.close', tabId: id })}
        onCreate={() => {
          setNewTabUrl('');
          setNewTabOpen(true);
        }}
      />

      {variant === 'operator' && (
        <AddressBar
          url={currentTab?.url ?? ''}
          enabled={can.navigate && isController && runtimeOpen && Boolean(tabId)}
          onNavigate={(url) => tabId && void run({ t: 'tab.navigate', tabId, url })}
          onBack={() => tabId && void run({ t: 'tab.back', tabId })}
          onForward={() => tabId && void run({ t: 'tab.forward', tabId })}
          onReload={() => tabId && void run({ t: 'tab.reload', tabId })}
        />
      )}

      <div className={cx('relative min-h-0 flex-1', variant === 'customer' && 'p-0')}>
        <ScreenCanvas
          frames={frames}
          tabId={tabId}
          fallbackSize={snap?.size ?? { width: 1280, height: 800 }}
          enabled={inputEnabled && !activeDialog}
          cursor={tabId ? state.cursors?.[tabId] : undefined}
          onCopy={snap?.browserTools ? copyRemote : undefined}
          dimmed={dimmed}
          onInput={onInput}
        >
          <ViewerOverlay
            state={state}
            isController={isController}
            done={done}
            onOpenBrowser={
              can.openBrowser ? () => void run({ t: 'browser.open' }, 'open') : undefined
            }
            onRequestControl={joined && !done ? requestControl : undefined}
            requesting={requesting}
          />
        </ScreenCanvas>
      </div>

      <div className="flex items-center gap-2 border-t border-line bg-surface px-3 py-2 text-xs">
        <Button
          size="sm"
          disabled={!inputEnabled || !snap?.browserTools || Boolean(activeDialog)}
          onClick={copyRemote}
        >
          선택 내용 복사
        </Button>
        <Button
          size="sm"
          disabled={!inputEnabled || Boolean(activeDialog)}
          onClick={() => {
            setClipboardText('');
            setClipboardMode('paste');
          }}
        >
          텍스트 붙여넣기
        </Button>
        <span className="text-ink-muted">
          {snap?.browserTools
            ? 'Ctrl/Cmd+C 복사 · Ctrl/Cmd+V 붙여넣기'
            : '알림·복사·커서 기능은 노드 업데이트 후 사용할 수 있습니다'}
        </span>
      </div>
      {activeDialog && (
        <BrowserDialogModal
          key={activeDialog.dialogId}
          dialog={activeDialog}
          socket={socket}
          enabled={joined && isController && nodeOnline && !done}
          onRequestControl={
            joined && !done && !requesting ? () => void requestControl() : undefined
          }
          onTakeControl={
            joined && can.forceControl && !done ? () => void run({ t: 'control.take' }) : undefined
          }
        />
      )}
      <Modal
        open={clipboardMode !== null}
        title={clipboardMode === 'copy' ? '원격 선택 내용' : '원격 페이지에 붙여넣기'}
        onClose={() => setClipboardMode(null)}
        footer={
          <>
            <Button onClick={() => setClipboardMode(null)}>닫기</Button>
            {clipboardMode === 'copy' ? (
              <CopyButton text={clipboardText} />
            ) : (
              <Button
                variant="primary"
                disabled={!inputEnabled || !clipboardText}
                onClick={() => {
                  if (tabId) onInput({ t: 'input.text', tabId, text: clipboardText });
                  setClipboardMode(null);
                }}
              >
                붙여넣기
              </Button>
            )}
          </>
        }
      >
        <textarea
          aria-label="클립보드 텍스트"
          className="min-h-40 w-full rounded border border-line p-3"
          value={clipboardText}
          maxLength={65536}
          readOnly={clipboardMode === 'copy'}
          onChange={(e) => setClipboardText(e.target.value)}
        />
      </Modal>

      {isController && (
        <FileChooserModal chooser={state.chooser} progress={state.progress} socket={socket} />
      )}

      <Modal
        open={newTabOpen}
        title="새 탭"
        onClose={() => setNewTabOpen(false)}
        size="sm"
        footer={
          <>
            <Button onClick={() => setNewTabOpen(false)}>취소</Button>
            <Button
              variant="primary"
              loading={busy === 'newtab'}
              onClick={async () => {
                const url = newTabUrl.trim();
                if (!url) return;
                if (await run({ t: 'tab.create', url }, 'newtab')) setNewTabOpen(false);
              }}
            >
              열기
            </Button>
          </>
        }
      >
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            const url = newTabUrl.trim();
            if (!url) return;
            if (await run({ t: 'tab.create', url }, 'newtab')) setNewTabOpen(false);
          }}
        >
          <Input
            autoFocus
            inputMode="url"
            placeholder="https://example.com"
            value={newTabUrl}
            onChange={(e) => setNewTabUrl(e.target.value)}
          />
          <p className="mt-2 text-xs text-ink-muted">http 또는 https 주소만 열 수 있습니다.</p>
        </form>
      </Modal>

      <ConfirmDialog
        open={confirmDone}
        title="작업을 완료하시겠습니까?"
        message="완료를 누르면 운영자에게 알림이 가고, 운영자가 이어서 설정 작업을 진행합니다. 화면은 계속 볼 수 있습니다."
        confirmLabel="완료"
        loading={busy === 'done'}
        onCancel={() => setConfirmDone(false)}
        onConfirm={async () => {
          if (await run({ t: 'customer.done' }, 'done')) {
            setDone(true);
            setConfirmDone(false);
          }
        }}
      />
    </div>
  );
}
